import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import componentPropertiesMap from './ComponentFormProperties.json';
import { useChangeBooleans, useComponentProperties, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { useEffect, useState } from "react";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import ChildrenTabManagementGrid from "./TabContainer/ChildrenTabManagementGrid";
import { DragAndDrop } from "@progress/kendo-react-common";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useTranslation } from "react-i18next";
import CreateTabsDialog from "./TabContainer/CreateTabsDialog";
import { isLanguagesChanged } from "../utils";

function TabContainer() {

    const formProperties = componentPropertiesMap.TabContainer;

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const resetContext = useResetComponentProperties();
    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);

    const [properties, setProperty] = useComponentProperties();
    const [{ isGeneralPropertiesChanged }, setChangeProperty] = useChangeBooleans();
    const [openTabDialog, setOpenTabDialog] = useState(false);

    const { t } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/tab_container"
    ], { useSuspense: false })

    useEffect(() => {
        setProperties();
        setInitializationDone(true);
    }, [])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    const [initializationDone, setInitializationDone] = useState(false);
    useEffect(() => {
        if (initializationDone) {
            setIsChanged();
        }
    }, [properties, selectedPage, selectedComponent]);

    // state
    const [tabSelected, setTabSelected] = useState(0);

    // functions
    function setProperties() {
        for (const property of formProperties) {
            if (
                selectedComponent[property] !== undefined
            ) {
                setProperty(property, selectedComponent[property])
            }
            setChangeProperty("isGeneralPropertiesChanged", false);
        }
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "children":
                    if (selectedComponent[property].length !== properties[property].length) {
                        isChanged = true;
                        break;
                    }
                    for (let childrenIndex = 0; childrenIndex < selectedComponent[property].length; childrenIndex++) {
                        const currentChildProperties = properties[property][childrenIndex];
                        const oldChildProperties = selectedComponent[property][childrenIndex];
                        if (
                            // hidden changed
                            currentChildProperties.hidden !== oldChildProperties.hidden ||
                            // child reordered
                            currentChildProperties.id !== oldChildProperties.id
                        ) {
                            isChanged = true;
                            break;
                        }
                        if (currentChildProperties.label !== oldChildProperties.label) {
                            isChanged = true;
                            break;
                        }
                        if (isLanguagesChanged(currentChildProperties.languages, oldChildProperties.languages)) {
                            isChanged = true;
                            break;
                        }
                    }
                    break;
                default:
                    if (selectedComponent[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    // handlers
    function handleTabSelect(event) {
        setTabSelected(event.selected);
    }

    async function handleSave() {
        await saveComponent();
        updatePageComponent(selectedComponent.id, properties);
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setProperties();
        }
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={true}>
                <TabStrip className="flex-grow-1 h-100 edit1" selected={tabSelected} onSelect={handleTabSelect}>
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <button
                            onClick={() => setOpenTabDialog(true)}
                            className="btn btn-outline-primary float-right me-3 mt-3"
                        >
                            <i className="fas fa-plus me-1"></i> {t("newTab", { ns: "display/components/tab_container" })}
                        </button>
                        {openTabDialog && <CreateTabsDialog onClose={() => setOpenTabDialog(false)} />}
                        <Scrollable>
                            {
                                initializationDone &&
                                <DragAndDrop>
                                    <ChildrenTabManagementGrid />
                                </DragAndDrop>
                            }
                        </Scrollable>
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default TabContainer;